import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { Table, Button, Form, Card, Badge, Accordion, Modal } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

const ManageCarModels = () => {
    const { user } = useContext(AuthContext);
    const [carModels, setCarModels] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [vehicleSizes, setVehicleSizes] = useState([]);
    const [sizeMappings, setSizeMappings] = useState({});
    const [savingSize, setSavingSize] = useState(null);
    const [showOrderEditor, setShowOrderEditor] = useState(false);

    const canManage = hasPermission(user?.role, 'settings:carmodels');
    const isSuperAdmin = hasPermission(user?.role, 'superadmin:access_shops');

    // 매장 전용 모델 추가/수정 모달
    const emptyModelForm = { id: null, manufacturer_id: '', modelName: '', vehicleClass: '' };
    const [showModelModal, setShowModelModal] = useState(false);
    const [modelForm, setModelForm] = useState(emptyModelForm);
    const [modelModalError, setModelModalError] = useState('');

    const openAddModel = () => { setModelModalError(''); setModelForm(emptyModelForm); setShowModelModal(true); };
    const openEditModel = (m) => {
        setModelModalError('');
        setModelForm({ id: m.id, manufacturer_id: m.manufacturer_id, modelName: m.modelName, vehicleClass: m.vehicleClass || '' });
        setShowModelModal(true);
    };

    const saveModel = async () => {
        setModelModalError('');
        if (!modelForm.manufacturer_id || !modelForm.modelName.trim() || !modelForm.vehicleClass.trim()) {
            setModelModalError('제조사·모델명·차급을 모두 입력해주세요.');
            return;
        }
        try {
            if (modelForm.id) {
                await api.put(`/carmodels/${modelForm.id}`, {
                    modelName: modelForm.modelName.trim(),
                    vehicleClass: modelForm.vehicleClass.trim(),
                });
            } else {
                await api.post('/carmodels', {
                    manufacturer_id: parseInt(modelForm.manufacturer_id),
                    modelName: modelForm.modelName.trim(),
                    vehicleClass: modelForm.vehicleClass.trim(),
                });
            }
            setShowModelModal(false);
            fetchData();
        } catch (error) {
            setModelModalError(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || '저장에 실패했습니다.');
        }
    };

    const toggleModelActive = async (m) => {
        const action = m.isActive ? '비활성화' : '활성화';
        if (!window.confirm(`'${m.modelName}' 모델을 ${action}하시겠습니까?`)) return;
        try {
            await api.put(`/carmodels/${m.id}/toggle`);
            fetchData();
        } catch (error) {
            console.error('Error toggling model active:', error);
        }
    };

    const fetchData = useCallback(async () => {
        if (!user) return;
        try {
            const [cmRes, mfrRes, sizeRes] = await Promise.all([
                api.get('/carmodels', { params: { showAll: '1' } }),
                api.get('/carmodels/manufacturers'),
                api.get('/vehiclesizes'),
            ]);
            setCarModels(cmRes.data.data);
            setManufacturers(mfrRes.data.data);
            setVehicleSizes(sizeRes.data.data.sizes || []);

            const mappings = {};
            cmRes.data.data.forEach(m => {
                if (m.shopSizeId) mappings[m.id] = m.shopSizeId;
            });
            setSizeMappings(mappings);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [user]);

    useEffect(() => {
        if (user) fetchData();
    }, [user, fetchData]);

    const visibleManufacturers = useMemo(() =>
        manufacturers.filter(m => m.isVisible).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)),
    [manufacturers]);
    const hiddenManufacturers = useMemo(() => manufacturers.filter(m => !m.isVisible), [manufacturers]);

    const groupedModels = useMemo(() => {
        const groups = {};
        carModels.forEach(m => {
            const mfr = m.manufacturer || '기타';
            if (!groups[mfr]) groups[mfr] = [];
            groups[mfr].push(m);
        });
        // 각 그룹 내에서 displayOrder로 정렬
        Object.values(groups).forEach(models => {
            models.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.modelName.localeCompare(b.modelName));
        });
        return groups;
    }, [carModels]);

    const orderedMfrNames = useMemo(() => {
        return visibleManufacturers.map(m => m.name).filter(name => groupedModels[name]);
    }, [visibleManufacturers, groupedModels]);

    const handleSizeChange = async (carModelId, sizeId) => {
        setSizeMappings(prev => ({ ...prev, [carModelId]: sizeId || null }));
        setSavingSize(carModelId);
        try {
            await api.put('/carmodels/model-settings', {
                settings: [{ car_model_id: carModelId, vehicle_size_id: sizeId || null }]
            });
        } catch (error) {
            console.error("Error saving size mapping:", error);
        } finally {
            setSavingSize(null);
        }
    };

    const handleModelVisibilityToggle = async (modelId, currentVisible) => {
        try {
            await api.put('/carmodels/model-settings', {
                settings: [{ car_model_id: modelId, isVisible: currentVisible ? 0 : 1 }]
            });
            fetchData();
        } catch (error) {
            console.error("Error toggling model visibility:", error);
        }
    };

    const handleMfrVisibilityToggle = async (manufacturerId, currentVisible) => {
        const mfr = manufacturers.find(m => m.id === manufacturerId);
        if (!mfr) return;
        try {
            await api.put('/carmodels/shop-settings', {
                settings: [{ manufacturer_id: manufacturerId, isVisible: currentVisible ? 0 : 1, displayOrder: mfr.displayOrder }]
            });
            fetchData();
        } catch (error) {
            console.error("Error toggling visibility:", error);
        }
    };

    const handleMfrDragEnd = async (result) => {
        if (!result.destination) return;
        const items = Array.from(visibleManufacturers);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);

        try {
            await api.put('/carmodels/shop-settings', {
                settings: items.map((m, i) => ({ manufacturer_id: m.id, isVisible: 1, displayOrder: i + 1 }))
            });
            fetchData();
        } catch (error) {
            console.error("Error saving order:", error);
        }
    };

    const handleModelDragEnd = async (result) => {
        if (!result.destination) return;
        const mfrName = result.source.droppableId;
        const models = Array.from(groupedModels[mfrName].filter(m => m.shopVisible !== 0));
        const [moved] = models.splice(result.source.index, 1);
        models.splice(result.destination.index, 0, moved);

        try {
            await api.put('/carmodels/model-settings', {
                settings: models.map((m, i) => ({ car_model_id: m.id, displayOrder: i + 1 }))
            });
            fetchData();
        } catch (error) {
            console.error("Error saving model order:", error);
        }
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>차종 관리</h3>
                    <p>제조사별로 사용할 차종을 관리하고, 매장 사이즈를 매핑하세요</p>
                </div>
                <div className="page-header-actions d-flex gap-2">
                    {canManage && !isSuperAdmin && (
                        <Button variant="primary" size="sm" onClick={openAddModel}>
                            + 우리 매장 모델 추가
                        </Button>
                    )}
                    {canManage && (
                        <Button variant={showOrderEditor ? 'primary' : 'outline-secondary'} size="sm"
                            onClick={() => setShowOrderEditor(!showOrderEditor)}>
                            {showOrderEditor ? '순서 편집 닫기' : '제조사 순서 편집'}
                        </Button>
                    )}
                </div>
            </div>

            {showOrderEditor && canManage && (
                <Card className="mb-3">
                    <Card.Header className="py-2">제조사 노출 순서 (드래그로 변경)</Card.Header>
                    <Card.Body className="py-2">
                        <DragDropContext onDragEnd={handleMfrDragEnd}>
                            <Droppable droppableId="manufacturers">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                        {visibleManufacturers.map((mfr, index) => (
                                            <Draggable key={String(mfr.id)} draggableId={String(mfr.id)} index={index}>
                                                {(provided, snapshot) => (
                                                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                                        className="d-flex align-items-center justify-content-between py-2 px-3 border-bottom"
                                                        style={{ ...provided.draggableProps.style, background: snapshot.isDragging ? '#e9ecef' : 'white' }}>
                                                        <span>☰ <strong className="ms-2">{mfr.name}</strong></span>
                                                        <Button variant="outline-danger" size="sm"
                                                            onClick={() => handleMfrVisibilityToggle(mfr.id, true)}>
                                                            숨기기
                                                        </Button>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Card.Body>
                </Card>
            )}

            {hiddenManufacturers.length > 0 && canManage && (
                <Card className="mb-3">
                    <Card.Header className="py-2">
                        <small className="text-muted">숨김 제조사 ({hiddenManufacturers.length}개)</small>
                    </Card.Header>
                    <Card.Body className="py-2">
                        {hiddenManufacturers.map(m => (
                            <Badge key={m.id} bg="secondary" className="me-2 mb-1" style={{ cursor: 'pointer' }}
                                onClick={() => handleMfrVisibilityToggle(m.id, false)}>
                                {m.name} +
                            </Badge>
                        ))}
                    </Card.Body>
                </Card>
            )}

            <DragDropContext onDragEnd={handleModelDragEnd}>
                <Accordion defaultActiveKey={orderedMfrNames[0]}>
                    {orderedMfrNames.map(mfrName => {
                        const allModels = groupedModels[mfrName] || [];
                        const visibleModels = allModels.filter(m => m.shopVisible !== 0);
                        const hiddenModels = allModels.filter(m => m.shopVisible === 0);

                        return (
                            <Accordion.Item eventKey={mfrName} key={mfrName}>
                                <Accordion.Header>
                                    {mfrName}
                                    <Badge bg="light" text="dark" className="ms-2">{visibleModels.length}개 모델</Badge>
                                    {hiddenModels.length > 0 && <Badge bg="secondary" className="ms-1">{hiddenModels.length} 숨김</Badge>}
                                </Accordion.Header>
                                <Accordion.Body className="p-0">
                                    <Table hover size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                {canManage && <th style={{ width: 30 }}></th>}
                                                <th>모델명</th>
                                                <th>차급</th>
                                                <th style={{ width: 160 }}>매장 사이즈</th>
                                                {canManage && <th style={{ width: 60 }}>노출</th>}
                                            </tr>
                                        </thead>
                                        <Droppable droppableId={mfrName}>
                                            {(provided) => (
                                                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                    {visibleModels.map((model, index) => (
                                                        <Draggable key={String(model.id)} draggableId={String(model.id)} index={index} isDragDisabled={!canManage}>
                                                            {(provided, snapshot) => (
                                                                <tr ref={provided.innerRef} {...provided.draggableProps}
                                                                    style={{ ...provided.draggableProps.style, background: snapshot.isDragging ? '#f0f0f0' : undefined }}>
                                                                    {canManage && <td {...provided.dragHandleProps} style={{ cursor: 'grab', textAlign: 'center' }}>☰</td>}
                                                                    <td>
                                                                        {model.modelName}
                                                                        {model.shopId && <Badge bg="info" className="ms-2">전용</Badge>}
                                                                    </td>
                                                                    <td><small className="text-muted">{model.vehicleClass}</small></td>
                                                                    <td>
                                                                        {canManage ? (
                                                                            <Form.Select size="sm"
                                                                                value={sizeMappings[model.id] || ''}
                                                                                onChange={(e) => handleSizeChange(model.id, e.target.value ? parseInt(e.target.value) : null)}
                                                                                disabled={savingSize === model.id}>
                                                                                <option value="">미지정</option>
                                                                                {vehicleSizes.map(s => (
                                                                                    <option key={s.id} value={s.id}>{s.sizeName}</option>
                                                                                ))}
                                                                            </Form.Select>
                                                                        ) : (
                                                                            <span>{vehicleSizes.find(s => s.id === sizeMappings[model.id])?.sizeName || '미지정'}</span>
                                                                        )}
                                                                    </td>
                                                                    {canManage && (
                                                                        <td className="text-center">
                                                                            <div className="d-flex gap-1 justify-content-center">
                                                                                {model.shopId && (
                                                                                    <>
                                                                                        <Button variant="outline-primary" size="sm"
                                                                                            onClick={() => openEditModel(model)}
                                                                                            title="매장 전용 모델 수정">✎</Button>
                                                                                        <Button variant="outline-danger" size="sm"
                                                                                            onClick={() => toggleModelActive(model)}
                                                                                            title="비활성화">⏻</Button>
                                                                                    </>
                                                                                )}
                                                                                <Button variant="outline-secondary" size="sm"
                                                                                    onClick={() => handleModelVisibilityToggle(model.id, true)}
                                                                                    title="숨기기">✕</Button>
                                                                            </div>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </tbody>
                                            )}
                                        </Droppable>
                                    </Table>
                                    {hiddenModels.length > 0 && canManage && (
                                        <div className="px-3 py-2 bg-light border-top">
                                            <small className="text-muted me-2">숨김 모델:</small>
                                            {hiddenModels.map(m => (
                                                <Badge key={m.id} bg="secondary" className="me-1 mb-1" style={{ cursor: 'pointer' }}
                                                    onClick={() => handleModelVisibilityToggle(m.id, false)}>
                                                    {m.modelName} +
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            </DragDropContext>

            {isSuperAdmin && (
                <Card className="mt-3">
                    <Card.Body className="text-muted text-center">
                        글로벌 차종 추가/수정은 슈퍼 관리자 페이지에서 가능합니다.
                    </Card.Body>
                </Card>
            )}

            {canManage && !isSuperAdmin && (
                <Card className="mt-3">
                    <Card.Body className="text-muted small">
                        <i className="bi bi-info-circle me-1"></i>
                        목록의 일반 차종은 전체 공통(글로벌)입니다. 목록에 없는 차는 <strong>+ 우리 매장 모델 추가</strong>로 등록하세요 — <Badge bg="info">전용</Badge> 표시가 붙고 우리 매장에서만 보입니다. 수정·비활성화는 전용 모델만 가능합니다.
                    </Card.Body>
                </Card>
            )}

            <Modal show={showModelModal} onHide={() => setShowModelModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modelForm.id ? '매장 전용 모델 수정' : '우리 매장 전용 모델 추가'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modelModalError && <div className="alert alert-danger py-2">{modelModalError}</div>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>제조사</Form.Label>
                            <Form.Select
                                value={modelForm.manufacturer_id}
                                onChange={(e) => setModelForm(prev => ({ ...prev, manufacturer_id: e.target.value }))}
                                disabled={!!modelForm.id}
                            >
                                <option value="">제조사 선택</option>
                                {visibleManufacturers.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </Form.Select>
                            {modelForm.id && <Form.Text className="text-muted">제조사는 변경할 수 없습니다. (다른 제조사면 새로 추가)</Form.Text>}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>모델명</Form.Label>
                            <Form.Control
                                value={modelForm.modelName}
                                onChange={(e) => setModelForm(prev => ({ ...prev, modelName: e.target.value }))}
                                placeholder="예: 모델Y, 그랜저 GN7"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>차급</Form.Label>
                            <Form.Control
                                value={modelForm.vehicleClass}
                                onChange={(e) => setModelForm(prev => ({ ...prev, vehicleClass: e.target.value }))}
                                placeholder="예: 준중형, SUV, 대형"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModelModal(false)}>닫기</Button>
                    <Button variant="primary" onClick={saveModel}>{modelForm.id ? '수정' : '추가'}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ManageCarModels;
